<template>
  <div class="public-tenders">
    <!-- Header -->
    <div class="header-content">
      <div class="container header-content">
        <h1>Public Tenders</h1>
        <p>Browse and apply for current tender opportunities</p>
      </div>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <div class="container">
        <div class="search-controls">
          <div class="search-input">
            <i class="fas fa-search"></i>
            <input
              v-model="searchParams.search"
              @input="debouncedSearch"
              placeholder="Search tenders..."
              type="text"
            />
          </div>
          <select v-model="searchParams.category" @change="fetchTenders">
            <option value="">All Categories</option>
            <option value="construction">Construction</option>
            <option value="supplies">Supplies</option>
            <option value="services">Services</option>
            <option value="consulting">Consulting</option>
            <option value="maintenance">Maintenance</option>
            <option value="technology">Technology</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tender List -->
    <div class="tenders-section">
      <div class="container">
        <div v-if="loading" class="loading">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading tenders...</p>
        </div>

        <div v-else-if="tenders.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <h3>No tenders found</h3>
          <p>There are currently no active tenders matching your criteria</p>
        </div>

        <div v-else class="tender-list">
          <div
            v-for="tender in tenders"
            :key="tender.id"
            class="tender-card"
            @click="viewTender(tender.slug)"
          >
            <div class="tender-card-grid">
              <div class="tender-left">
                <div class="countdown">
                  <div class="countdown-days">{{ computeRemaining(tender.submissionDeadline).days }}</div>
                  <div class="countdown-label">days</div>
                </div>
              </div>
              <div class="tender-main">
                <div class="tender-header">
                  <div class="tender-ref">{{ tender.referenceNumber }}</div>
                  <div class="tender-category">{{ tender.category }}</div>
                </div>
                <h3>{{ tender.title }}</h3>
                <p class="tender-description">{{ tender.description }}</p>
                <div class="tender-meta">
                  <div class="meta-item">
                    <i class="fas fa-calendar-alt"></i>
                    <span>Deadline: {{ formatDate(tender.submissionDeadline) }}</span>
                  </div>
                  <div class="meta-item" v-if="tender.estimatedValue">
                    <i class="fas fa-dollar-sign"></i>
                    <span>{{ formatCurrency(tender.estimatedValue, tender.currency) }}</span>
                  </div>
                </div>
              </div>
              <div class="tender-actions">
                <button @click.stop="viewTender(tender.slug)" class="btn btn-primary">
                  View Details & Apply
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="btn btn-outline"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info">
            Page {{ currentPage }} of {{ totalPages }} ({{ totalTenders }} tenders)
          </span>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="btn btn-outline"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { debounce } from 'lodash'
import { tenderApi } from '@/api/tender'

export default {
  name: 'PublicTenders',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    
    const searchParams = reactive({
      search: '',
      category: '',
      page: 1,
      limit: 12,
    })
    
    const tenders = ref([])
    const totalTenders = ref(0)
    const totalPages = ref(0)
    
    const currentPage = computed(() => searchParams.page)
    
    const fetchTenders = async () => {
      loading.value = true
      try {
        const response = await tenderApi.getPublicTenders(searchParams)
        tenders.value = response.data.data
        totalTenders.value = response.data.total
        totalPages.value = response.data.totalPages
      } catch (error) {
        console.error('Error fetching tenders:', error)
      } finally {
        loading.value = false
      }
    }
    
    const debouncedSearch = debounce(() => {
      searchParams.page = 1
      fetchTenders()
    }, 500)
    
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        searchParams.page = page
        fetchTenders()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    
    const viewTender = (slug) => {
      router.push(`/tenders/${slug}`)
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }
    
    const formatCurrency = (amount, currency) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount)
    }
    
    const getDaysRemaining = (deadline) => {
      const now = new Date()
      const deadlineDate = new Date(deadline)
      const diffTime = deadlineDate - now
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return Math.max(0, diffDays)
    }

    function computeRemaining(deadline) {
      if (!deadline) return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      const end = new Date(deadline).getTime()
      let diff = Math.max(0, end - Date.now())
      const secs = Math.floor(diff / 1000)
      const days = Math.floor(secs / 86400)
      const hours = Math.floor((secs % 86400) / 3600)
      const minutes = Math.floor((secs % 3600) / 60)
      const seconds = secs % 60
      return { days, hours, minutes, seconds }
    }
    
    onMounted(() => {
      fetchTenders()
    })
    
    return {
      loading,
      searchParams,
      tenders,
      totalTenders,
      totalPages,
      currentPage,
      fetchTenders,
      debouncedSearch,
      goToPage,
      viewTender,
      formatDate,
      formatCurrency,
      getDaysRemaining,
      computeRemaining,
    }
  },
}
</script>

<style scoped>
.public-tenders {
  min-height: 100vh;
  background: #f8fafc;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.header {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  padding: 60px 0;
}

.header h1 {
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.header p {
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
}

.search-section {
  background: white;
  padding: 32px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-controls {
  display: flex;
  gap: 14px;
  align-items: center;
}

.search-input {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  z-index: 1;
}

.search-input input {
  width: 100%;
  padding: 14px 14px 14px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}

.search-input input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select {
  padding: 14px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-width: 160px;
}

select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.tenders-section {
  padding: 48px 0;
}

.loading {
  text-align: center;
  padding: 48px;
  color: #666;
}

.loading i {
  font-size: 24px;
  margin-bottom: 14px;
}

.empty-state {
  text-align: center;
  padding: 48px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 14px;
  color: #d1d5db;
}

.tender-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
}

.tender-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
}

.tender-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: #3b82f6;
}

.tender-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.tender-ref {
  font-family: monospace;
  font-size: 12px;
  color: #3b82f6;
  background: #eff6ff;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
}

.tender-category {
  color: #666;
  font-size: 14px;
  text-transform: capitalize;
  background: #f3f4f6;
  padding: 4px 12px;
  border-radius: 14px;
}

.tender-content h3 {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #1a1a1a;
  line-height: 1.4;
}

.tender-description {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 14px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tender-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
}

.meta-item i {
  width: 14px;
  color: #3b82f6;
}

.tender-actions {
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.btn-outline {
  border: 1px solid #e5e7eb;
  color: #374151;
  background: white;
}

.btn-outline:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-top: 48px;
}

.page-info {
  color: #666;
  font-size: 14px;
}

@media (max-width: 768px) {
  .header {
    padding: 40px 0;
  }

  .header h1 {
    font-size: 28px;
  }

  .header p {
    font-size: 14px;
  }

  .search-controls {
    flex-direction: column;
    gap: 12px;
  }

  .search-input {
    max-width: none;
  }

  .tender-list {
    grid-template-columns: 1fr;
  }

  .tender-meta {
    gap: 6px;
  }

  .meta-item {
    font-size: 12px;
  }
}
</style>